/**
 * Entity Configuration Provider
 * Allows projects to configure available entity types
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface EntityTypeMetadata {
  type: string;
  label: string;
  category: string;
}

export interface EntityConfig {
  availableTypes: string[];
  tableColumns?: Record<string, string[]>;
  typeMetadata?: Record<string, Omit<EntityTypeMetadata, 'type'>>; // type -> { label, category }
}

interface EntityConfigContextType {
  config: EntityConfig;
  registerEntityTypes: (types: string[]) => void;
  registerTableColumns: (entityType: string, columns: string[]) => void;
  registerEntityTypeMetadata: (metadata: EntityTypeMetadata[]) => void;
  getEntityLabel: (type: string) => string;
  getEntityCategory: (type: string) => string;
}

// Default entity types that work across projects
const DEFAULT_ENTITY_TYPES: string[] = [
  'alert',
  'unified_alert',
  'document',
  'document_registry',
  'compliance',
  'incident',
  'inspection',
  'equipment',
  'maintenance_log',
];

const EntityConfigContext = createContext<EntityConfigContextType | undefined>(undefined);

export function EntityConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<EntityConfig>({
    availableTypes: DEFAULT_ENTITY_TYPES,
    tableColumns: {},
    typeMetadata: {},
  });

  // Listen for app config entity types being set
  useEffect(() => {
    const checkForConfigTypes = () => {
      const configTypes = (window as any).__tracekit_entity_types;
      if (configTypes && Array.isArray(configTypes) && configTypes.length > 0) {
        console.log('[EntityConfigProvider] Loading entity types from config:', configTypes);
        setConfig(prev => ({
          ...prev,
          availableTypes: configTypes,
        }));
      }
    };

    // Check immediately
    checkForConfigTypes();
    
    // Check again after a brief delay to catch async config loading
    const timeout = setTimeout(checkForConfigTypes, 100);
    
    return () => clearTimeout(timeout);
  }, []);

  const registerEntityTypes = (types: string[]) => {
    console.log('[EntityConfigProvider] Registering entity types:', types.length);
    setConfig((prev) => ({
      ...prev,
      availableTypes: types,
    }));
  };

  const registerTableColumns = (entityType: string, columns: string[]) => {
    setConfig((prev) => ({
      ...prev,
      tableColumns: {
        ...prev.tableColumns,
        [entityType]: columns,
      },
    }));
  };

  const registerEntityTypeMetadata = (metadata: EntityTypeMetadata[]) => {
    setConfig((prev) => {
      const newMetadata: Record<string, Omit<EntityTypeMetadata, 'type'>> = { ...prev.typeMetadata };
      metadata.forEach(m => {
        newMetadata[m.type] = { label: m.label, category: m.category };
      });
      return {
        ...prev,
        typeMetadata: newMetadata,
      };
    });
  };

  const getEntityLabel = (type: string): string => {
    return config.typeMetadata?.[type]?.label || type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const getEntityCategory = (type: string): string => {
    return config.typeMetadata?.[type]?.category || 'Other';
  };

  return (
    <EntityConfigContext.Provider value={{ 
      config, 
      registerEntityTypes, 
      registerTableColumns,
      registerEntityTypeMetadata,
      getEntityLabel,
      getEntityCategory,
    }}>
      {children}
    </EntityConfigContext.Provider>
  );
}

export function useEntityConfig() {
  const context = useContext(EntityConfigContext);
  if (!context) {
    throw new Error('useEntityConfig must be used within EntityConfigProvider');
  }
  return context;
}





